import { IconBrightness } from "@tabler/icons-react"
import { useRouter } from "@tanstack/react-router"
import { Avatar, AvatarFallback, AvatarImage } from "design-system/atoms/Avatar"
import { Switch } from "design-system/atoms/Switch"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "design-system/organisms/Sidebar"
import { useTheme } from "design-system/theme"
import { cn } from "design-system/utils"
import {
  Building2,
  Check,
  Languages,
  Lock,
  LogOutIcon,
  MoreVerticalIcon,
  SquareTerminal,
} from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useSwitchOrganizationMutation } from "modules/auth/hooks/useSwitchOrganizationMutation"
import { useAuth } from "modules/shared/hooks/useAuth"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import { useUpdateEmployeeMutation } from "modules/shared/hooks/useUpdateEmployeeMutation"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { BackofficeType, Locale } from "schema"

type NavUserProps = {
  setIsChangePasswordModalOpen: (value: boolean) => void
}

export function NavUser({ setIsChangePasswordModalOpen }: NavUserProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const { theme, toggleTheme } = useTheme()
  const { developerMode, setDeveloperMode } = useDeveloperMode()

  const { data: employee } = useAuthEmployeeQuery()
  const { onLogout } = useAuth()
  const { isMobile } = useSidebar()
  const { mutateAsync } = useUpdateEmployeeMutation()
  const { mutate: switchOrganization } = useSwitchOrganizationMutation()

  const organizations = employee?.organizations ?? []
  const hasMultipleOrganizations = organizations.length > 1

  const employeeInitials = useMemo(
    () =>
      employee?.name
        ?.split(" ")
        .map((name) => name.charAt(0))
        .join(""),
    [employee?.name],
  )

  const shouldShowCompany = useMemo(() => {
    return (employee?.organization.companies.length ?? 0) === 1
  }, [employee?.organization.companies])

  const displayEntity = useMemo(() => {
    if (shouldShowCompany && employee?.company) {
      return {
        name: employee.company.name,
        logo: employee.company.logo,
      }
    }
    return {
      name: employee?.organization.name,
      logo: employee?.organization.logo,
    }
  }, [
    shouldShowCompany,
    employee?.company,
    employee?.organization.name,
    employee?.organization.logo,
  ])

  const displayEntityInitials = useMemo(
    () => (displayEntity.name ? getInitials(displayEntity.name) : undefined),
    [displayEntity.name],
  )

  const onChangeLocale = async (locale: keyof typeof Locale) => {
    await mutateAsync({ locale })
  }

  const onSwitchOrganization = (organizationId: string) => {
    if (organizationId === employee?.organizationId) return
    switchOrganization({ organizationId })
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="group-data-[collapsible=icon]:flex hover:bg-primary/5 data-[state=open]:bg-primary/5 hover:text-primary active:bg-primary/5 data-[state=open]:text-primary"
            >
              <Avatar
                className={cn(
                  "h-8 w-8 rounded-md bg-muted",
                  "group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:h-10 transition-all duration-300 group-data-[collapsible=icon]:mx-auto",
                )}
              >
                <AvatarImage
                  src={displayEntity.logo ?? undefined}
                  alt={displayEntity.name ?? undefined}
                />
                <AvatarFallback className="rounded-md bg-foreground/10">
                  {displayEntityInitials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-medium">{displayEntity.name}</span>
                <span className="truncate text-xs text-muted-foreground">{employee?.name}</span>
              </div>
              <MoreVerticalIcon className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage
                    src={employee?.avatar ?? undefined}
                    alt={employee?.name ?? undefined}
                  />
                  <AvatarFallback className="rounded-lg">{employeeInitials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{employee?.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{employee?.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Languages />
                {t("shared.mySettings.locale")}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  {Object.keys(Locale).map((locale) => (
                    <DropdownMenuItem
                      key={locale}
                      onClick={() => onChangeLocale(locale as keyof typeof Locale)}
                      className="flex justify-between"
                    >
                      {t(`shared.localeOptions.${locale}`)}
                      {employee?.locale === locale && <Check size={16} />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            {hasMultipleOrganizations && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Building2 />
                  {t("shared.mySettings.organizations")}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {organizations.map((organization) => (
                      <DropdownMenuItem
                        key={organization.id}
                        onClick={() => onSwitchOrganization(organization.id)}
                      >
                        <Avatar className="h-6 w-6 rounded-md bg-muted">
                          <AvatarImage
                            src={organization.logo ?? undefined}
                            alt={organization.name}
                          />
                          <AvatarFallback className="rounded-md bg-foreground/10 text-xs">
                            {getInitials(organization.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate">{organization.name}</span>
                        {employee?.organizationId === organization.id && (
                          <Check size={16} className="ml-auto" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}
            <DropdownMenuItem onClick={() => setIsChangePasswordModalOpen(true)}>
              <Lock />
              {t("shared.mySettings.changePassword")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                toggleTheme()
                e.preventDefault()
              }}
            >
              <IconBrightness />
              <span>{t("shared.mode")}</span>
              <Switch
                className="ml-auto"
                checked={theme === "dark"}
                onCheckedChange={toggleTheme}
              />
            </DropdownMenuItem>
            {employee?.backofficeType === BackofficeType.Superadmin && (
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault()
                  setDeveloperMode(!developerMode)
                }}
              >
                <SquareTerminal />
                <span>{t("shared.developerMode")}</span>
                <Switch
                  className="ml-auto"
                  checked={developerMode}
                  onCheckedChange={setDeveloperMode}
                  onClick={(event) => event.stopPropagation()}
                />
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onLogout(router)}>
              <LogOutIcon />
              {t("shared.mySettings.logOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase()
}
